import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatCompact, formatINR } from "@/lib/api";
import AIDrawer from "@/components/AIDrawer";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChevronRight, ChevronDown, BookOpenText, Download, FileSpreadsheet, Search, Loader2, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { key: "not_due", label: "Not Due", color: "#10B981" },
  { key: "b0_30", label: "0-30", color: "#3B82F6" },
  { key: "b31_60", label: "31-60", color: "#F59E0B" },
  { key: "b61_90", label: "61-90", color: "#F97316" },
  { key: "b91_180", label: "91-180", color: "#EF4444" },
  { key: "b180_plus", label: "180+", color: "#7F1D1D" },
];
const CATS = [
  { key: "all", label: "All" },
  { key: "balance", label: "Balance" },
  { key: "retention", label: "Retention" },
  { key: "advance", label: "Advance" },
];

const fmtD = (v) => new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const DAY = 86400000;
const bucketOf = (dateStr) => {
  const od = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(dateStr).getTime()) / DAY);
  if (od <= 0) return "not_due";
  if (od <= 30) return "b0_30";
  if (od <= 60) return "b31_60";
  if (od <= 90) return "b61_90";
  if (od <= 180) return "b91_180";
  return "b180_plus";
};

export default function AgeingPage({ side }) {
  const reportKey = side === "AR" ? "ar-ageing" : "ap-ageing";
  const partyLabel = side === "AR" ? "Customer" : "Supplier";
  const ledgerPath = side === "AR" ? "/reports/ar-ledger" : "/reports/ap-ledger";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");
  const [ageBy, setAgeBy] = useState("due");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [party, setParty] = useState("");
  const [msmeFilter, setMsmeFilter] = useState("all");
  const [beyond45, setBeyond45] = useState(false);
  const [bucketSel, setBucketSel] = useState("");
  const [expanded, setExpanded] = useState({});
  const [limit, setLimit] = useState(400);
  const [exporting, setExporting] = useState(false);
  const [msmeExporting, setMsmeExporting] = useState(false);
  const [stmtBusy, setStmtBusy] = useState("");

  const downloadStatement = async (code) => {
    setStmtBusy(code);
    try {
      const res = await fetch(`${API}/reports/${side === "AR" ? "ar-ledger" : "ap-ledger"}/statement?party=${encodeURIComponent(code)}`, { credentials: "include" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Statement export failed");
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DeePiping_Statement_${code}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally { setStmtBusy(""); }
  };

  useEffect(() => {
    setData(null); setError(""); setTab("all"); setAgeBy("due"); setSearch(""); setFrom(""); setTo("");
    setParty(""); setMsmeFilter("all"); setBeyond45(false); setBucketSel(""); setExpanded({}); setLimit(400);
    api.get(`/reports/${reportKey}`).then(({ data }) => setData(data)).catch((e) => setError(e.response?.data?.detail || e.message));
  }, [reportKey]);

  const msmeInfo = useMemo(() => {
    const by = {};
    (data?.rows || []).forEach((r) => { by[r.party_code] = { msme: r.msme, udyam: r.udyam, interest: r.est_interest || 0 }; });
    return by;
  }, [data]);

  const baseInv = useMemo(() => {
    if (!data) return [];
    const out = [];
    data.rows.forEach((r) =>
      r.invoices.forEach((inv) =>
        out.push({ ...inv, party_code: r.party_code, party_name: r.party_name,
          bucket: bucketOf(ageBy === "due" ? inv.due_date : inv.doc_date) })
      )
    );
    return out.filter((i) => {
      if (party && i.party_code !== party) return false;
      if (from && i.doc_date < from) return false;
      if (to && i.doc_date > to) return false;
      const m = msmeInfo[i.party_code];
      if (msmeFilter === "msme" && !m?.msme) return false;
      if (msmeFilter === "non" && m?.msme) return false;
      if (beyond45 && !(m?.msme && i.overdue_days > 45 && i.amount > 0)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!i.party_name.toLowerCase().includes(q) && !i.doc_no.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [data, party, from, to, search, ageBy, msmeFilter, beyond45, msmeInfo]);

  const tabInv = useMemo(() => (tab === "all" ? baseInv : baseInv.filter((i) => i.category === tab)), [baseInv, tab]);
  const finalInv = useMemo(() => (bucketSel ? tabInv.filter((i) => i.bucket === bucketSel) : tabInv), [tabInv, bucketSel]);

  const cards = useMemo(() => {
    const c = { balance: { total: 0, count: 0 }, retention: { total: 0, count: 0 }, advance: { total: 0, count: 0 } };
    baseInv.forEach((i) => { c[i.category].total += i.amount; c[i.category].count += 1; });
    return c;
  }, [baseInv]);

  const chartData = useMemo(() =>
    BUCKETS.map((b) => ({ ...b, value: tabInv.filter((i) => i.bucket === b.key).reduce((s, i) => s + i.amount, 0) })),
  [tabInv]);

  const partyRows = useMemo(() => {
    const by = {};
    finalInv.forEach((i) => {
      const p = by[i.party_code] || (by[i.party_code] = { party_code: i.party_code, party_name: i.party_name, total_due: 0, open_items: 0, invoices: [], ...Object.fromEntries(BUCKETS.map((b) => [b.key, 0])) });
      p[i.bucket] += i.amount; p.total_due += i.amount; p.open_items += 1; p.invoices.push(i);
    });
    return Object.values(by).sort((a, b) => b.total_due - a.total_due);
  }, [finalInv]);

  const totalOut = tabInv.reduce((s, i) => s + i.amount, 0);

  const exportCsv = () => {
    const head = ["Code", partyLabel, side === "AP" ? "MSME" : null, ...BUCKETS.map((b) => b.label), "Total Due", "Items"].filter(Boolean).join(",");
    const body = partyRows.map((r) => [r.party_code, `"${r.party_name}"`,
      side === "AP" ? (msmeInfo[r.party_code]?.udyam || "No") : null,
      ...BUCKETS.map((b) => r[b.key]), r.total_due, r.open_items].filter((v) => v !== null).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([head + "\n" + body], { type: "text/csv" }));
    a.download = `DeePiping_${reportKey}.csv`; a.click();
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/reports/${reportKey}/export`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `DeePiping_${reportKey}.xlsx`; a.click();
    } finally { setExporting(false); }
  };
  const exportMsme = async () => {
    setMsmeExporting(true);
    try {
      const res = await fetch(`${API}/reports/ap-ageing/msme-export`, { credentials: "include" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "MSMED export failed");
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "DeePiping_MSMED_Compliance.xlsx"; a.click();
    } finally { setMsmeExporting(false); }
  };

  if (error) return <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-4 max-w-lg">{error}</div>;
  if (!data) return <div className="text-slate-500 text-sm animate-pulse">Loading {side} ageing…</div>;

  const parties = data.rows.map((r) => ({ code: r.party_code, name: r.party_name }));
  const input = "bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50";
  const cardDefs = side === "AR"
    ? [["balance", "Balance (RV / DR)", "DEBIT"], ["retention", "Retention (RT / SGL H)", "DEBIT"], ["advance", "Advance (DZ / SGL A)", "DEBIT"]]
    : [["balance", "Balance (KR / RE)", "CREDIT"], ["retention", "Retention (RT / SGL H)", "CREDIT"], ["advance", "Advance (KZ / SGL A)", "CREDIT"]];
  const msme = data.msme;
  const msmeStatus = side === "AP" ? data.msme_status : null;

  return (
    <div className="space-y-5" data-testid={`report-page-${reportKey}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{side} Ageing</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            {side === "AR" ? "Accounts receivable" : "Accounts payable"} ageing — drill down {partyLabel} → Invoice · ageing by <span className="font-semibold text-slate-700">{ageBy === "due" ? "due date (SAP net due date)" : "invoice date"}</span>
            <span data-testid={`${reportKey}-source-chip`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${data.data_source === "SAP" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
              {data.data_source === "SAP" ? "SAP Live · Read-only" : "Mock Data"}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid={`${reportKey}-export-csv-button`} className="flex items-center gap-1.5 text-xs bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 transition-colors"><Download size={12} /> CSV</button>
          <button onClick={exportExcel} disabled={exporting} data-testid={`${reportKey}-export-excel-button`} className="flex items-center gap-1.5 text-xs bg-emerald-700/90 hover:bg-emerald-600 disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors">{exporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel</button>
          {side === "AP" && (
            <button onClick={exportMsme} disabled={msmeExporting} data-testid="msme-compliance-export-button" title="MSMED Act compliance — MSME vendor dues with >45-day flag and estimated interest" className="flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors">{msmeExporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} MSMED Excel</button>
          )}
          <AIDrawer reportKey={reportKey} reportTitle={`${side} Ageing`} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid={`${reportKey}-filters`}>
        <span className={`${input} text-slate-500 select-none`}>As on: Today</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${reportKey}-date-from`} className={input} title="Invoice date from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${reportKey}-date-to`} className={input} title="Invoice date to" />
        <div className="flex items-center rounded-md border border-slate-300 overflow-hidden" data-testid={`${reportKey}-ageby-toggle`}>
          <span className="px-2 text-[11px] text-slate-400 select-none">Age by</span>
          {[["due", "Due date"], ["invoice", "Invoice date"]].map(([k, l]) => (
            <button key={k} onClick={() => setAgeBy(k)} data-testid={`${reportKey}-ageby-${k}`}
              className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${ageBy === k ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid={`${reportKey}-search-input`} placeholder="Search SAP ref / invoice no" className={`${input} pl-8 w-52`} />
        </div>
        <select value={party} onChange={(e) => setParty(e.target.value)} data-testid={`${reportKey}-party-select`} className={input}>
          <option value="">{side === "AR" ? "All Customers" : "All Suppliers"}</option>
          {parties.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
        {side === "AP" && msme && (
          <>
            <select value={msmeFilter} onChange={(e) => setMsmeFilter(e.target.value)} data-testid="ap-msme-filter" className={input}>
              <option value="all">MSME: All</option>
              <option value="msme">MSME only</option>
              <option value="non">Non-MSME</option>
            </select>
            <button onClick={() => setBeyond45(!beyond45)} data-testid="ap-msme-45-chip"
              className={`flex items-center gap-1.5 text-xs rounded-md border px-2.5 py-1.5 transition-colors ${beyond45 ? "bg-[#B01E24] border-[#B01E24] text-white" : "bg-white border-slate-300 text-slate-600 hover:border-[#B01E24]/40"}`}>
              <ShieldAlert size={12} /> MSME &gt; 45 days
            </button>
          </>
        )}
        <div className="flex rounded-md border border-slate-300 overflow-hidden ml-auto" data-testid={`${reportKey}-doc-tabs`}>
          {CATS.map((c) => (
            <button key={c.key} onClick={() => setTab(c.key)} data-testid={`${reportKey}-tab-${c.key}`}
              className={`px-3.5 py-1.5 text-xs font-medium transition-colors ${tab === c.key ? "bg-[#B01E24] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {msmeStatus?.error && (
        <div className="flex items-start gap-2 text-[12px] text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="ap-msme-status-error">
          <span className="font-semibold shrink-0">MSME lookup ({msmeStatus.configured_field}):</span>
          <span>{msmeStatus.error}</span>
        </div>
      )}
      {msmeStatus && !msmeStatus.error && msmeStatus.entity && (
        <div className="text-[11.5px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 inline-block" data-testid="ap-msme-status-ok">
          MSME field <span className="font-mono">{msmeStatus.configured_field}</span> → SAP <span className="font-mono">{msmeStatus.entity}.{msmeStatus.resolved_field}</span> · {msmeStatus.suppliers_marked} supplier(s) marked MSME
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cardDefs.map(([k, title, dc]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4" data-testid={`${reportKey}-card-${k}`}>
            <div className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">{title} · <span className="text-slate-400">{dc}</span></div>
            <div className="mt-1.5 text-xl font-bold text-slate-900 font-mono">{formatCompact(cards[k].total)}</div>
            <div className="text-xs text-slate-500 mt-0.5">{cards[k].count} invoices</div>
          </div>
        ))}
      </div>

      {side === "AP" && msme && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-4 flex flex-wrap items-center gap-4" data-testid="ap-msme-strip">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10.5px] font-semibold uppercase tracking-widest text-amber-800 flex items-center gap-1.5"><ShieldAlert size={13} /> MSME dues beyond the 45-day limit</div>
            <div className="text-sm text-amber-900 mt-1">
              <span className="font-bold font-mono">{formatCompact(msme.beyond_45)}</span> principal across {msme.beyond_45_count} invoices ·
              estimated MSMED Act interest <span className="font-bold font-mono">~{formatCompact(msme.est_interest)}</span>
              <span className="text-amber-700"> (≈ 20.25% p.a. = 3× RBI bank rate)</span>
            </div>
            <div className="text-[11px] text-amber-700 mt-0.5">{formatCompact(msme.outstanding)} total outstanding to registered MSME / Udyam vendors · {msme.invoices} open invoices</div>
          </div>
          <button onClick={() => { setBeyond45(true); setMsmeFilter("msme"); }} data-testid="ap-msme-show-invoices"
            className="text-xs font-medium bg-[#B01E24] hover:bg-[#9A171D] text-white rounded-md px-3.5 py-2 transition-colors">
            Show only these invoices
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Ageing Buckets</h3>
            <span className="text-[11px] text-slate-400">Click a bar to filter the drill-down</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => formatCompact(v)} width={72} />
              <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatCompact(v)} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} onClick={(d) => setBucketSel(bucketSel === d.key ? "" : d.key)} className="cursor-pointer">
                {chartData.map((b) => <Cell key={b.key} fill={b.color} opacity={bucketSel && bucketSel !== b.key ? 0.3 : 1} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5" data-testid={`${reportKey}-summary-panel`}>
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Summary</h3>
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-sm text-slate-500">Total Outstanding</span>
            <span className="text-xl font-bold text-slate-900 font-mono">{formatCompact(totalOut)}</span>
          </div>
          <div className="space-y-2">
            {chartData.map((b) => (
              <div key={b.key} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />{b.label}</span>
                <span className="font-mono text-slate-700">{formatCompact(b.value)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-400">{tabInv.length} open invoices</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {bucketSel && (
          <button onClick={() => setBucketSel("")} data-testid={`${reportKey}-clear-bucket`} className="inline-flex items-center gap-1.5 text-xs bg-[#B01E24]/10 text-[#B01E24] border border-[#B01E24]/20 rounded-full px-3 py-1">
            Bucket: {BUCKETS.find((b) => b.key === bucketSel)?.label} <X size={11} />
          </button>
        )}
        {beyond45 && (
          <button onClick={() => setBeyond45(false)} data-testid="ap-clear-45" className="inline-flex items-center gap-1.5 text-xs bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-3 py-1">
            MSME &gt; 45 days <X size={11} />
          </button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">{partyLabel} — Drill-down</h3>
          <p className="text-[11px] text-slate-400">Expand a {partyLabel.toLowerCase()} to see open invoices · Ledger opens the full line-item view</p>
        </div>
        <div className="overflow-x-auto report-scroll">
          <table className="w-full text-[13px]" data-testid={`${reportKey}-table`}>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="w-8" />
                <th className="text-left px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Code</th>
                <th className="text-left px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{partyLabel}</th>
                <th className="w-20" />
                {BUCKETS.map((b) => <th key={b.key} className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{b.label}</th>)}
                <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Total</th>
                <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Items</th>
              </tr>
            </thead>
            <tbody>
              {partyRows.slice(0, limit).map((r) => (
                <tr key={r.party_code} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`${reportKey}-row-${r.party_code}`}
                  onClick={() => setExpanded((e) => ({ ...e, [r.party_code]: !e[r.party_code] }))}>
                  <td className="pl-3 text-slate-400">{expanded[r.party_code] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                  <td className="px-3 py-2 font-mono text-[11.5px] text-slate-500 whitespace-nowrap" data-testid={`${reportKey}-code-${r.party_code}`}>{r.party_code}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 border border-slate-200 rounded px-1 py-0.5 mr-2">{partyLabel}</span>
                    <span className="text-slate-800 font-medium">{r.party_name}</span>
                    {side === "AP" && msme && (
                      msmeInfo[r.party_code]?.msme ? (
                        <>
                          <span data-testid={`msme-badge-${r.party_code}`} className="ml-2 text-[9.5px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">MSME · {msmeInfo[r.party_code].udyam}</span>
                          {msmeInfo[r.party_code].interest > 0 && (
                            <span className="ml-1.5 text-[9.5px] font-medium bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5">Interest ~{formatCompact(msmeInfo[r.party_code].interest)}</span>
                          )}
                        </>
                      ) : (
                        <span className="ml-2 text-[9.5px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">MSME: No</span>
                      )
                    )}
                  </td>
                  <td className="px-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`${ledgerPath}?q=${encodeURIComponent(r.party_name)}`); }}
                        data-testid={`${reportKey}-ledger-${r.party_code}`}
                        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#B01E24] border border-slate-200 hover:border-[#B01E24]/30 rounded-md px-2 py-0.5 transition-colors">
                        <BookOpenText size={11} /> Ledger
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); downloadStatement(r.party_code); }}
                        data-testid={`${reportKey}-stmt-${r.party_code}`} disabled={stmtBusy === r.party_code}
                        title="Download Statement of Account (Excel)"
                        className="flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 border border-emerald-200 hover:border-emerald-400 rounded-md px-2 py-0.5 transition-colors disabled:opacity-50">
                        {stmtBusy === r.party_code ? <Loader2 size={11} className="animate-spin" /> : <FileSpreadsheet size={11} />} Stmt
                      </button>
                    </div>
                  </td>
                  {BUCKETS.map((b) => <td key={b.key} className="px-3 py-2 text-right font-mono text-[12px] text-slate-600 whitespace-nowrap">{r[b.key] ? formatCompact(r[b.key]) : "—"}</td>)}
                  <td className="px-3 py-2 text-right font-mono text-[12px] font-semibold text-slate-900 whitespace-nowrap">{formatCompact(r.total_due)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[12px] text-slate-500">{r.open_items}</td>
                </tr>
              )).flatMap((row, idx) => {
                const r = partyRows.slice(0, limit)[idx];
                if (!expanded[r.party_code]) return [row];
                return [row, (
                  <tr key={`${r.party_code}-inv`} className="border-b border-slate-100 bg-slate-50/70">
                    <td />
                    <td colSpan={11} className="px-3 py-2">
                      <table className="w-full text-[12px]">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                            <th className="text-left py-1">Invoice</th><th className="text-left py-1">Doc Type</th>
                            <th className="text-left py-1">Invoice Date</th><th className="text-left py-1">Due Date</th>
                            <th className="text-right py-1">Overdue (d)</th>
                            <th className="text-left py-1 pl-3">Bucket</th><th className="text-right py-1">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.invoices.map((i) => (
                            <tr key={i.doc_no} className="border-t border-slate-200/60">
                              <td className="py-1.5 font-mono text-slate-600">{i.doc_no}</td>
                              <td className="py-1.5 text-slate-500">{i.doc_type}</td>
                              <td className="py-1.5 text-slate-500">{fmtD(i.doc_date)}</td>
                              <td className="py-1.5 text-slate-500">{fmtD(i.due_date)}</td>
                              <td className={`py-1.5 text-right font-mono ${i.overdue_days > 45 ? "text-red-600" : "text-slate-500"}`}>{i.overdue_days > 0 ? i.overdue_days : "—"}</td>
                              <td className="py-1.5 pl-3"><span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-white" style={{ background: BUCKETS.find((b) => b.key === i.bucket)?.color }}>{BUCKETS.find((b) => b.key === i.bucket)?.label}</span></td>
                              <td className="py-1.5 text-right font-mono text-slate-700">{formatINR(i.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )];
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200">
                <td />
                <td colSpan={2} className="px-3 py-2.5 font-semibold text-[12px] text-slate-900">TOTAL</td>
                <td />
                {BUCKETS.map((b) => <td key={b.key} className="px-3 py-2.5 text-right font-mono text-[12px] font-semibold text-slate-900 whitespace-nowrap">{formatCompact(partyRows.reduce((s, r) => s + r[b.key], 0))}</td>)}
                <td className="px-3 py-2.5 text-right font-mono text-[12px] font-semibold text-slate-900 whitespace-nowrap">{formatCompact(partyRows.reduce((s, r) => s + r.total_due, 0))}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[12px] font-semibold text-slate-900">{finalInv.length}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {partyRows.length > limit && (
          <div className="flex items-center justify-center gap-3 px-4 py-2.5 border-t border-slate-200 bg-slate-50/60">
            <span className="text-xs text-slate-500">Showing {limit.toLocaleString("en-IN")} of {partyRows.length.toLocaleString("en-IN")} parties</span>
            <button onClick={() => setLimit((l) => l + 1000)} data-testid={`${reportKey}-show-more-button`}
              className="text-xs font-medium text-[#B01E24] border border-[#B01E24]/30 rounded-full px-3.5 py-1 hover:bg-[#B01E24]/5 transition-colors">
              Show 1,000 more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
